<template>
  <main class="avfallsbeholder-side">
    <system-notification class="mb-size-30" komponent-id="avfall-og-gjenvinning" />
    <hgroup>
      <h1 class="ods-text--size-echo ods-margin-top-0 ods-margin-bottom-3 ods-text--size-alfa-breakpoint-medium">
        {{ $t('avfallOgGjenvinning.title') }}
      </h1>
      <h2 class="ods-text--size-golf ods-margin-top-0 ods-margin-bottom-5 ods-text--size-charlie-breakpoint-medium">
        {{ $t('avfallOgGjenvinning.subtitle') }}
      </h2>
    </hgroup>
    <div v-if="eiendommerLoading" class="mb-size-30">
      <ods-spinner />
      {{ $t('eiendomsvelger.eiendommer.loading') }}
    </div>
    <ods-status-message
      v-else-if="eiendommerError"
      class="mb-size-30"
      tittel="Melding om advarsel"
      :melding="$t('eiendomsvelger.eiendommer.error')"
      meldingstype="warning"
    />
    <template v-else>
      <section
        v-if="eiendommer.length > 0 || folkeregistrertAdresse.hasOwnProperty('erFolkeregistrert')"
        class="ods-padding-3 ods-padding-6-breakpoint-medium ods-margin-bottom-6 local-section local-section--gray mb-size-30"
      >
        <eiendoms-velger
          v-if="folkeregistrertAdresse && (valgtAdresse || valgtAdresse === 0)"
          :valgt-adresse="valgtAdresse"
          :eiendommer-filtrert="eiendommerUtenFolkeregistrert"
          @selectedAddress="velgAdresse"
          @oppdater-valgt-adresse="oppdatertValgtAdresse"
        />
        <mine-beholdere v-if="selectedAdresse" />
        <mine-varsler-tomminger
          v-if="currentRepresentation.erPrivatPerson && selectedAdresse && !renovasjonsbeholdereLaster"
          :eiendom="selectedAdresse"
        />
      </section>
      <ods-status-message
        v-else
        class="mb-size-30"
        tittel="Melding om advarsel"
        :melding="'Vi kunne ikke finne noen eiendommer og kan derfor ikke vise deg noen renovasjonstjenester'"
        meldingstype="warning"
      />
      <abonnements-info v-if="currentRepresentation.erPrivatPerson" class="ods-margin-bottom-6" />
      <section class="lenker">
        <h2 class="ods-text--size-golf ods-margin-top-0 ods-margin-bottom-5 ods-text--size-charlie-breakpoint-medium">
          Relevante lenker
        </h2>
        <div class="ods-grid ods-grid--gap">
          <div class="ods-grid__column--12 ods-grid__column--4-breakpoint-medium">
            <div class="kontaktoss-lenker">
              <h3
                class="ods-text--size-india ods-text--weight-medium ods-margin-top-0 ods-margin-bottom-5 ods-text--size-golf-breakpoint-medium"
              >
                Kontakt Renovasjons- og gjenvinningsetaten
              </h3>
              <ul class="lenkeliste">
                <li class="lenkeliste__lenke ods-margin-bottom-3">
                  <a href="tel:23483650" class="lenke">Ring kundesenteret på tlf.: 23 48 36 50</a>
                </li>
                <li class="lenkeliste__lenke ods-margin-bottom-3">
                  <a href="mailto:postmottak@reg.oslo.kommune.no" class="lenke">
                    Send e-post til postmottak@reg.oslo.kommune.no
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div
            v-if="currentRepresentation.erPrivatPerson"
            class="ods-grid__column--12 ods-grid__column--4-breakpoint-medium"
          >
            <div v-if="!eiendommerLoading && !renovasjonsbeholdereLaster" class="veileder-lenker">
              <h3
                class="ods-text--size-india ods-text--weight-medium ods-margin-top-0 ods-margin-bottom-5 ods-text--size-golf-breakpoint-medium"
              >
                Endre dine renovasjonstjenester
              </h3>
              <ul class="lenkeliste">
                <li class="lenkeliste__lenke ods-margin-bottom-3">
                  <a
                    :href="`${environments.VUE_APP_VEILEDER_URL}/#?id=1139`"
                    class="lenke"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Endre størrelse på beholder
                  </a>
                </li>
                <li class="lenkeliste__lenke ods-margin-bottom-3">
                  <a
                    :href="`${environments.VUE_APP_VEILEDER_URL}/#?id=1139`"
                    class="lenke"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Erstatte ødelagt beholder
                  </a>
                </li>
                <li class="lenkeliste__lenke ods-margin-bottom-3">
                  <a
                    :href="`${environments.VUE_APP_VEILEDER_URL}/#?id=5132`"
                    class="lenke"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Opprette, endre eller avslutte renovasjonsabonnement
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="ods-grid__column--12 ods-grid__column--4-breakpoint-medium">
            <div class="felles-veileder-lenker">
              <h3
                class="ods-text--size-india ods-text--weight-medium ods-margin-top-0 ods-margin-bottom-5 ods-text--size-golf-breakpoint-medium"
              >
                Relatert informasjon
              </h3>
              <ul class="lenkeliste">
                <li class="lenkeliste__lenke ods-margin-bottom-1">
                  <a
                    href="https://www.oslo.kommune.no/avfall-og-gjenvinning/"
                    class="lenke"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Mer informasjon om renovasjon
                    <span class="inline-icon inline-icon--super ods-inline-block">
                      <span
                        class="ods-icon ods-icon--align-right ods-icon--small ods-icon--size-lima ods-text--weight-bold ods-icon--new-window"
                        aria-hidden="true"
                      />
                    </span>
                  </a>
                </li>
                <li class="lenkeliste__lenke ods-margin-bottom-1">
                  <a
                    href="https://www.oslo.kommune.no/avfall-og-gjenvinning/gjenbruksstasjoner/"
                    class="lenke"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Se alle gjenbruksstasjoner
                    <span class="inline-icon inline-icon--super ods-inline-block">
                      <span
                        class="ods-icon ods-icon--align-right ods-icon--small ods-icon--size-lima ods-text--weight-bold ods-icon--new-window"
                        aria-hidden="true"
                      />
                    </span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </template>
  </main>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import {
  opprettMatrikkeladresse,
  opprettVegadresse,
  sjekkGateadresse,
  sjekkMatrikkeladresse,
} from '@/utility/eiendommerUtils';
import EiendomsVelger from '@/components/Eiendomsvelger.vue';
import MineVarslerTomminger from '@/components/MineVarslerTomminger.vue';
import MineBeholdere from '@/components/MineBeholdere.vue';
import SystemNotification from '@/global-components/SystemNotification.vue';
import OdsSpinner from '@/global-components/OdsSpinner.vue';
import AbonnementsInfo from '@/components/abonnementer/AbonnementsInfo.vue';
import OdsStatusMessage from '@/global-components/OdsStatusMessage.vue';

export default {
  name: 'AvfallOgGjenvinningMain',

  components: {
    AbonnementsInfo,
    EiendomsVelger,
    MineBeholdere,
    MineVarslerTomminger,
    OdsSpinner,
    SystemNotification,
    OdsStatusMessage,
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (vm.getOrgsToRepresent.length === 0) {
        vm.setBacklink({
          text: vm.$t('breadcrumb.foreldreside.text'),
          href: vm.$t('breadcrumb.foreldreside.href'),
        });
      } else {
        vm.setBacklink({
          text: vm.$t('breadcrumb.representasjonsside.text'),
          href: '/',
        });
      }
    });
  },

  data: () => ({
    selectedAdresse: undefined,
    valgtAdresse: null,
  }),

  computed: {
    ...mapState(['environments']),
    ...mapState('eiendommer', ['eiendommer', 'eiendommerLoading', 'eiendommerError', 'folkeregistrertAdresse']),
    ...mapState('avfallOgGjenvinning', [
      'renovasjonsbeholdere',
      'renovasjonsbeholdereLaster',
      'renovasjonsbeholdereFeiler',
    ]),
    ...mapState('representasjoner', ['currentRepresentation']),
    ...mapGetters('eiendommer', ['eiendommerUtenFolkeregistrert']),
    ...mapGetters('representasjoner', ['getOrgsToRepresent']),
  },

  async mounted() {
    await this.getAlleEiendommer();
    if (this.$route.query && this.$route.query.eiendom && this.eiendommer.length > 0) {
      this.sjekkQueryParametere();
    } else {
      this.valgtAdresse = 'bolig';
    }
  },

  methods: {
    ...mapActions(['setBacklink']),
    ...mapActions('avfallOgGjenvinning', ['hentRenovasjonsInnhold']),
    ...mapActions('eiendommer', ['getAlleEiendommer', 'settAktivEiendom']),

    hentEksternId(eiendom) {
      const { vegadresse } = eiendom.postadresser[0];
      const vegadr = `${vegadresse.gate.replace(/\s/g, '_')},${vegadresse.husnr || ''}${
        vegadresse.bokstav ? `,${vegadresse.bokstav}` : ''
      }`;
      if (eiendom.eierEiendom) {
        const gbs = `${eiendom.matrikkeladresse.gardsnr}.${eiendom.matrikkeladresse.bruksnr}${
          eiendom.matrikkeladresse.seksjonsnr ? `.${eiendom.matrikkeladresse.seksjonsnr}` : ''
        }`;
        return `${gbs}:${vegadr}`;
      }
      return `:${vegadr}`;
    },

    velgAdresse(eiendom) {
      if (eiendom && eiendom.postadresser && eiendom.postadresser.length > 0 && eiendom.postadresser[0].vegadresse) {
        this.selectedAdresse = eiendom;
        const eksternId = this.hentEksternId(eiendom);
        const { vegadresse } = eiendom.postadresser[0];
        const gbs = `${eiendom.matrikkeladresse.gardsnr}.${eiendom.matrikkeladresse.bruksnr}${
          eiendom.matrikkeladresse.seksjonsnr ? `.${eiendom.matrikkeladresse.seksjonsnr}` : ''
        }`;
        const vegadr = `${vegadresse.gate} ${vegadresse.husnr || ''}${
          vegadresse.bokstav ? ` ${vegadresse.bokstav}` : ''
        }`;

        const eiendomsObjekt = {
          postadresse: vegadresse,
          vegadresse: vegadr,
          eierEiendom: eiendom.eierEiendom,
          gbs,
          eksternId,
        };

        this.settAktivEiendom(eiendomsObjekt);
        this.hentRenovasjonsInnhold(eiendomsObjekt);
      }
    },

    oppdatertValgtAdresse(adresse) {
      this.valgtAdresse = adresse;
    },

    /*
     * eiendom-query kan være en streng `matrikkeladresse:gateadresse` som feks `1.2.3:Brekkeliveien,20,a`
     * Den kan enten være bare med gateadresse-delen slik `:Gateadresse,tall,husnr`
     * Eller så er hele strengen med, sammen med matrikkeladressen
     */
    sjekkQueryParametere() {
      const { eiendom } = this.$route.query;
      let vegadresse = null;

      // Hvis parameter er bare gateadresse uten matrikkeladresse
      if (eiendom.startsWith(':')) {
        vegadresse = opprettVegadresse(eiendom.substr(1));

        // Sjekk om vegadressen er en del av folkeregistrert adresse
        if (
          this.folkeregistrertAdresse &&
          this.folkeregistrertAdresse.postadresser &&
          this.folkeregistrertAdresse.postadresser[0].vegadresse &&
          vegadresse
        ) {
          const gate = sjekkGateadresse(this.folkeregistrertAdresse.postadresser[0].vegadresse, vegadresse);

          if (gate) {
            this.valgtAdresse = 'bolig';
          } else {
            this.valgtAdresse = this.eiendommerUtenFolkeregistrert.findIndex((e) =>
              e.postadresser.length > 0 ? sjekkGateadresse(e.postadresser[0].vegadresse, vegadresse) : false
            );
          }
        } else {
          this.valgtAdresse = this.eiendommerUtenFolkeregistrert.findIndex((e) =>
            e.postadresser.length > 0 ? sjekkGateadresse(e.postadresser[0].vegadresse, vegadresse) : false
          );
        }
      } else {
        // Hvis både matrikkel og vegadresse er med
        const matrikkeladresse = opprettMatrikkeladresse(eiendom.split(':')[0]);
        vegadresse = opprettVegadresse(eiendom.split(':')[1]);

        // Sjekk om matrikkeladresse og vegadresse er en del av folkeregistrert adresse
        if (
          this.folkeregistrertAdresse &&
          this.folkeregistrertAdresse.postadresser &&
          this.folkeregistrertAdresse.postadresser.length > 0 &&
          this.folkeregistrertAdresse.matrikkeladresse &&
          matrikkeladresse
        ) {
          const matrikkel = sjekkMatrikkeladresse(this.folkeregistrertAdresse.matrikkeladresse, matrikkeladresse);
          const gate = sjekkGateadresse(this.folkeregistrertAdresse.postadresser[0].vegadresse, vegadresse);

          if (matrikkel && gate) {
            this.valgtAdresse = 'bolig';
          } else {
            this.valgtAdresse = this.eiendommerUtenFolkeregistrert.findIndex((e) => {
              if (e.postadresser.length > 0) {
                return (
                  sjekkGateadresse(e.postadresser[0].vegadresse, vegadresse) &&
                  sjekkMatrikkeladresse(e.matrikkeladresse, matrikkeladresse)
                );
              }
              return false;
            });
          }
        } else {
          // Sjekk om vegadressen ligger i eiendommer-arrayen uten folkeregistrert adresse
          this.valgtAdresse = this.eiendommerUtenFolkeregistrert.findIndex((e) =>
            e.postadresser.length > 0 ? sjekkGateadresse(e.postadresser[0].vegadresse, vegadresse) : false
          );
        }
      }

      // Hvis ingen eiendommer finnes, sett valgtAdresse til defaultverdi bolig slik at eiendomsvelgeren ikke kræsjer
      if (this.valgtAdresse === -1) {
        this.valgtAdresse = 'bolig';
      }
    },
  },
};
</script>

<style lang="scss">
@use 'sass:map';

.avfallsbeholder-side {
  .lenkeliste {
    &__lenke {
      overflow-wrap: anywhere;
    }
    a {
      word-wrap: inherit !important;
    }
  }
}
</style>
